import * as React from 'react';
import ReactDOM from 'react-dom';

import RootUtility from '~/app/root-utility.jsx';
import { useStoreValues } from '~/runtime';
import Navigation from './navigation/navigation';
import { BnDynamicComponent } from '~/shared/bn-dynamic-component';
import { publicLitPaths, publicReactPaths } from './public-paths';
import { getRootElement } from './get-root-element';
import { BnErrorBoundary } from '~/shared/bn-error-boundary';
import '~/translations/i18n';
import { TranslationsEditProvider, TranslationsEditSidebar, useTranslationsEditContextSelector } from '~/translations';
import classNames from 'classnames';

const config = JSON.parse(import.meta.env.VITE_PROJECT_AWS_SSM_CONFIG);
const litConfig = {
  href: config['api-root'],
  authConfig: JSON.stringify(config.auth),
};

const loadReactRoot = async () => (await import('./root-react-component.jsx')).default;
const loadLitRoot = async () => (await import('./binsentry-ui.jsx')).default;
const load3dToolsRoot = async () => (await import('~/old/3d-advanced-view/BinSentry-ui-3d-advanced-view.jsx')).default;

const reactPaths = [
  'account',
  'animal-groups',
  'inventory',
  'barns',
  'bins',
  'billing',
  'billing-management',
  'qbo-billing',
  'data-labeling-review',
  'device-analysis',
  'customers',
  'insights',
  'events',
  'feed-budgets',
  'feed-levels',
  'notifications',
  'feed-orders',
  'feeds',
  'order-scheduler',
  'organizations',
  'orders',
  'readings',
  'work-order-locations',
  'work-orders-prototype',
  'internal',
  'print',
];

function PageContainer({ children }) {
  const [editMode] = useTranslationsEditContextSelector(['editMode']);
  // The div container is important as we are in a display: grid context, so we want to encapsulate
  // all view components into a single node.
  // It is styled with static styles defined in index.html
  return <BnErrorBoundary locationTag='page-container'>
    <div className={classNames('page-container', editMode && 'edit-mode')}>
      {children}
    </div>
  </BnErrorBoundary>;
}

function TopLevelRouter () {
  const { path } = useStoreValues(['path']);
  const firstPathSegment = (path || '').split('/')[1];

  if (firstPathSegment === 'print') {
    return <PageContainer>
      <BnDynamicComponent getComponent={loadReactRoot} componentProperties={config} />
    </PageContainer>;
  }

  if (firstPathSegment === '3d-image-tools') {
    return <PageContainer>
      <BnDynamicComponent getComponent={load3dToolsRoot} componentProperties={config} />
    </PageContainer>;
  }

  if (publicReactPaths.includes(firstPathSegment)) {
    return <PageContainer>
      <BnDynamicComponent getComponent={loadReactRoot} componentProperties={config} />
    </PageContainer>;
  }

  if (publicLitPaths.includes(firstPathSegment)) {
    return <PageContainer>
      <BnDynamicComponent getComponent={loadLitRoot} componentProperties={litConfig} />
    </PageContainer>;
  }

  if (reactPaths.includes(firstPathSegment)) {
    return <Navigation {...config} >
      <PageContainer>
        <BnDynamicComponent getComponent={loadReactRoot} componentProperties={config} />
      </PageContainer>
    </Navigation>;
  }

  return <Navigation {...config} >
    <PageContainer>
      <BnDynamicComponent getComponent={loadLitRoot} componentProperties={litConfig} />
    </PageContainer>
  </Navigation>;
}

ReactDOM.render(
  <React.StrictMode>
    <BnErrorBoundary locationTag='root'>
      <TranslationsEditProvider apiRoot={config['api-root']} >
        <React.Suspense fallback={<div/>}>
          <RootUtility {...config} />
          <TranslationsEditSidebar />
          <TopLevelRouter />
        </React.Suspense>
      </TranslationsEditProvider>
    </BnErrorBoundary>
  </React.StrictMode>,
  getRootElement(),
);
